import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-07 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/oriora_round_card/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so data stays on-device.
// The regex intent-router below is the FREE FALLBACK (offline / signed-out / out of
// funds) — chat never goes dead. Original prototype notes:
//   • "log <course> <score> [9 holes] [putts N] [gir N]" → add_round   (local, free)
//   • "<course> putts N" / "set putts N for <course>"    → edit_round  (local, free)
//   • "conditions <x> for <course>" / "note <course>: …" → edit_round  (local, free)
//   • "add <club> to bag" / "retire <club>"              → add_bag     (local, free)
//   • "delete <course> round"                            → delete_round (UI confirms)
//   • "show 9-hole rounds / at <course> / <term>"        → filter      (local, free)
//   • "summarize / overview"                             → local stats (local, free)
//   • "recap my season"                                  → recapSeason (the ONE billed call)
//   • coaching / handicap ask                            → none + THE FENCE refusal
//
// THE FENCE: Round Card is CAN-only / observational. Any coaching, club pick, swing fix, or
// official/tournament handicap-index request is refused HERE (via brain.ts::fenceCheck) BEFORE
// EITHER path — the routing call (billed) or the recap call (billed) — mirroring the
// pre-graduation design, which ran the fence first on every message before any branch.

import { store, todayLocal, fmtShortDate, type Round, type NewRound, type NewBagChange, type RoundPatch } from "../store";
import { BrainError, fenceCheck, brainCall } from "../brain";
import type { ChatAction, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // COACHING FENCE: kept "" — never surface the model's freeform reply.
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage/editRound build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth on top of fenceCheck + reply-discard).
const SCOPE =
  "Hard limits: never coach, never give swing/technique/club/putting advice or tips, never compute or estimate an " +
  "official handicap or handicap index — decline plainly. This app is CAN-only: log golf rounds and bag clubs, " +
  "edit/search/delete them, and give an OBSERVATIONAL recap of the user's OWN logged rounds (scoring trend, " +
  "fairway/GIR rates, courses, bag evolution); back up to their own cloud.";

const HELP =
  "I can log rounds, edit them, search, summarize, recap your season, update your bag, and delete rounds — just tell me. Try:\n" +
  '• "log Pebble Beach 82" · "log 9 holes at Riverside 41 putts 15"\n' +
  '• "Pebble Beach putts 30" · "note Pebble Beach: windy back nine"\n' +
  '• "add hybrid to bag" · "retire 3-iron"\n' +
  '• "show 9-hole rounds" · "summarize" · "recap my season"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Forgiving round lookup by course: an exact course still wins, otherwise the FLEET fuzzy resolver
// (see ./fuzzy) tolerates misspellings, dropped words and word order. Rounds key on `course`, so we
// adapt them to the resolver's `name`. Resolution stays entirely client-side (the data is on-device).
async function findRounds(term: string, filter?: (r: Round) => boolean): Promise<Round[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listRounds({ limit: 50000 });
  const scoped = filter ? all.filter(filter) : all;
  const exact = scoped.filter((r) => r.course.toLowerCase() === q);
  if (exact.length) return exact;
  return findNamedMatches(scoped.map((r) => ({ ...r, name: r.course })), term);
}

/** "Couldn't find a round at X" enriched with the closest course names ("did you mean …?"). */
async function roundsNotFound(term: string): Promise<string> {
  const all = await store.listRounds({ limit: 50000 });
  return notFoundMessage(all.map((r) => ({ name: r.course })), term);
}

function pickRounds(rows: Round[]): string {
  return rows.slice(0, 5).map((r) => `• ${r.course || "Unnamed course"} — ${fmtShortDate(r.played_on)} (${r.total_score})`).join("\n");
}

function parseRound(raw: string): NewRound {
  let rest = ` ${raw.trim()} `;
  const grab = (re: RegExp): string | undefined => {
    const m = rest.match(re);
    if (m) { rest = rest.replace(m[0], " "); return m[1]; }
    return undefined;
  };
  const out: NewRound = { course: "" };
  const holes = grab(/\b(9|18)\s*(?:holes?|hole|h)\b/i);
  if (holes) out.holes_played = parseInt(holes, 10);
  const putts = grab(/\bputts?\s+(\d{1,3})\b/i) ?? grab(/\b(\d{1,3})\s*putts?\b/i);
  if (putts) out.putts = parseInt(putts, 10);
  const gir = grab(/\bgir\s+(\d{1,2})\b/i);
  if (gir) out.greens_in_reg = parseInt(gir, 10);
  // total score = the LAST standalone 2–3 digit number left in the string
  const score = rest.match(/\b(\d{2,3})\b(?![\s\S]*\b\d{2,3}\b)/);
  if (score) { out.total_score = parseInt(score[1], 10); rest = rest.replace(score[0], " "); }
  out.course = rest.replace(/\s{2,}/g, " ").trim().replace(/^(?:at|for|@)\s+/i, "").replace(/\s+(?:at|for)$/i, "").trim();
  return out;
}

// Reconstruct the current bag locally (replay add/retire) — for the "what's in my bag" reply.
async function currentBag(): Promise<string[]> {
  const changes = await store.listBagChanges();
  const ordered = [...changes].sort((a, b) => (a.changed_on < b.changed_on ? -1 : a.changed_on > b.changed_on ? 1 : a.created_at.localeCompare(b.created_at)));
  const set = new Map<string, string>();
  for (const c of ordered) {
    const key = c.club.trim().toLowerCase();
    if (!key) continue;
    if (c.action === "retired") set.delete(key); else set.set(key, c.club.trim());
  }
  return [...set.values()];
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // THE FENCE — pre-LLM, free, on BOTH paths. A coaching/handicap ask is refused
  // deterministically before it can reach EITHER billed call (routing OR recap).
  const fv = fenceCheck(text);
  if (!fv.ok) { const r = none(fv.message); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (course text → id) locally, because
// the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.round-card.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_round", description: "Log a completed round of golf.", params: { course: "the course name", total_score: "REQUIRED total strokes for the round", holes_played: "9 or 18, defaults to 18 if not said", putts: "optional total putts", greens_in_reg: "optional greens in regulation count", course_rating: "optional course rating for the tee, e.g. 71.3 (needed for the handicap estimate)", slope: "optional slope for the tee, e.g. 128 (needed for the handicap estimate)", conditions: "optional weather/course conditions note", notes: "optional free note" } },
  { name: "handicap", description: "Report the user's ESTIMATED handicap index, computed on-device from their logged 18-hole rounds that have a course rating + slope. Use for 'what's my handicap', 'my index'. Observational statistic, not an official/certified handicap.", params: {} },
  { name: "edit_round", description: "Change the putts, conditions, or note on an existing logged round. Identify it by course.", params: { course: "which round — course name as the user wrote it", putts: "new total putts, only if changing", conditions: "new conditions text, only if changing", notes: "new note text, only if changing" } },
  { name: "delete_round", description: "Delete one logged round identified by its course (the app asks the user to confirm first).", params: { course: "which round — course name as the user wrote it" } },
  { name: "add_bag", description: "Add a club to the bag, or mark one retired.", params: { club: "the club name", action: 'either "added" (default) or "retired"' } },
  { name: "show_bag", description: "List the clubs currently in the bag. No params.", params: {} },
  { name: "delete_bag_change", description: "Remove a logged bag change (a club-added or club-retired record) — the app asks the user to confirm first. Identify it by the club name.", params: { club: "the club on the bag-change record to remove" } },
  { name: "filter", description: "Search / list the user's logged rounds.", params: { course: "optional course name text to filter by", holes: "optional 9 or 18 to filter by round length", text: "optional free text to search course/conditions/notes/partners" } },
  { name: "recap", description: "Narrate the season: scoring trend, fairway/GIR rates, courses played, bag evolution — computed on-device, observational only. Never coaches or computes an official handicap. No params.", params: {} },
  { name: "summarize", description: "Quick season glance: rounds count, average, best score, estimated index vs par, last played — computed on-device. No params.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE rounds collection instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const rounds = await store.listRounds({ limit: 40 });
  const courses = [...new Set(rounds.map((r) => r.course).filter(Boolean))].slice(0, 30);
  const bag = await currentBag();
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Courses played: ${courses.join(", ") || "(none yet)"}. Current bag: ${bag.join(", ") || "(empty)"}. Lower score is better.`)
    + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  // COACHING FENCE: never surface the model's freeform reply — reply stays "".
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text);
    actions.push(one.action);
  }
  return { reply: "", actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asInt = (v: unknown, lo: number, hi: number): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n >= lo && n <= hi ? Math.round(n) : undefined;
};
const asFloat = (v: unknown, lo: number, hi: number): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n >= lo && n <= hi ? n : undefined;
};

export async function toAction(r: ServerChat, userText: string): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_round": {
      const course = asStr(p.course);
      if (!course) return none(say || 'Which course? Try "log Pebble Beach 82".');
      const total_score = asInt(p.total_score, 1, 300);
      if (total_score == null) return none(say || `What did you shoot at ${course}? Try "log ${course} 82".`);
      const input: NewRound = { course, total_score };
      const holes = asInt(p.holes_played, 9, 18);
      if (holes === 9 || holes === 18) input.holes_played = holes;
      const putts = asInt(p.putts, 0, 200);
      if (putts != null) input.putts = putts;
      const gir = asInt(p.greens_in_reg, 0, 18);
      if (gir != null) input.greens_in_reg = gir;
      const rating = asFloat(p.course_rating, 50, 90);
      if (rating != null) input.course_rating = rating;
      const slope = asInt(p.slope, 55, 155);
      if (slope != null) input.slope = slope;
      const conditions = asStr(p.conditions);
      if (conditions) input.conditions = conditions;
      const notes = asStr(p.notes);
      if (notes) input.notes = notes;
      return { reply: say, action: { kind: "add_round", input, label: course } };
    }
    case "handicap":
      return { reply: say, action: { kind: "handicap" } };
    case "edit_round": {
      const term = asStr(p.course);
      if (!term) return none(say || "Which round do you want to edit? Give me the course.");
      const rows = await findRounds(term);
      if (rows.length === 0) return none(await roundsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} rounds matching "${term}" — be more specific:\n${pickRounds(rows)}`);
      const patch: RoundPatch = {};
      const bits: string[] = [];
      const putts = asInt(p.putts, 0, 200);
      if (putts != null) { patch.putts = putts; bits.push(`${putts} putts`); }
      const conditions = asStr(p.conditions);
      if (conditions) { patch.conditions = conditions; bits.push(conditions); }
      const notes = asStr(p.notes);
      if (notes) { patch.notes = notes; bits.push("note"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — putts, conditions, or a note?");
      return { reply: say, action: { kind: "edit_round", id: rows[0].id, patch, label: `${rows[0].course} → ${bits.join(" · ")}` } };
    }
    case "delete_round": {
      const term = asStr(p.course);
      if (!term) return none(say || "Which round do you want to delete? Give me the course.");
      const rows = await findRounds(term);
      if (rows.length === 0) return none(await roundsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} rounds matching "${term}" — be more specific:\n${pickRounds(rows)}`);
      return { reply: say, action: { kind: "delete_round", id: rows[0].id, label: `${rows[0].course} (${fmtShortDate(rows[0].played_on)})` } };
    }
    case "add_bag": {
      const club = asStr(p.club);
      if (!club) return none(say || 'Which club? Try "add hybrid to bag".');
      const actionWord = asStr(p.action)?.toLowerCase();
      const bagAction = actionWord === "retired" || actionWord === "retire" ? "retired" : "added";
      const input: NewBagChange = { action: bagAction, club, changed_on: todayLocal() };
      return { reply: say, action: { kind: "add_bag", input, label: club } };
    }
    case "show_bag": {
      const clubs = await currentBag();
      const msg = clubs.length
        ? `You have ${clubs.length} club${clubs.length !== 1 ? "s" : ""} in the bag:\n${clubs.map((c) => `• ${c}`).join("\n")}`
        : "Your bag is empty — try \"add driver to bag\".";
      return none(msg);
    }
    case "delete_bag_change": {
      const club = asStr(p.club);
      if (!club) return none(say || "Which bag change should I remove? Name the club.");
      const changes = await store.listBagChanges();
      const matches = changes.filter((c) => c.club.toLowerCase().includes(club.toLowerCase()));
      if (matches.length === 0) return none(`I couldn't find a bag change for "${club}".`);
      if (matches.length > 1) return none(`I found ${matches.length} bag changes matching "${club}" — be more specific:\n${matches.map((c) => `• ${c.club} — ${c.action} (${fmtShortDate(c.changed_on)})`).join("\n")}`);
      const c = matches[0];
      return { reply: say, action: { kind: "delete_bag_change", id: c.id, label: `${c.club} (${c.action})` } };
    }
    case "filter": {
      const course = asStr(p.course)?.toLowerCase();
      const holes = asInt(p.holes, 9, 18);
      const text = asStr(p.text)?.toLowerCase();
      const pred = (r: Round): boolean => {
        if ((holes === 9 || holes === 18) && r.holes_played !== holes) return false;
        if (course && !r.course.toLowerCase().includes(course)) return false;
        if (text) {
          const hay = `${r.course} ${r.conditions} ${r.notes} ${r.partners.join(" ")}`.toLowerCase();
          if (!hay.includes(text)) return false;
        }
        return true;
      };
      return { reply: "", action: { kind: "filter", pred, label: say || userText } };
    }
    case "recap":
      return { reply: say, action: { kind: "recap" } };
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 0) THE FENCE — never let the chat coach, pick clubs, fix a swing, or produce a handicap index.
  const fv = fenceCheck(t);
  if (!fv.ok) return { reply: "", action: { kind: "none", message: fv.message } };

  // 1) SUMMARIZE — local season glance (free).
  if (/\b(summar(?:y|ise|ize)|overview|how\s+am\s+i\s+doing|my\s+stats)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 2) RECAP — the ONE billed call (season-wide, derived signals only).
  if (/\brecap\b/i.test(low)) {
    return { reply: "", action: { kind: "recap" } };
  }

  // 3) SHOW BAG — reconstruct + list the current clubs (local, free).
  if (/\b(what'?s?\s+in\s+my\s+bag|show\s+(?:me\s+)?(?:my\s+)?bag|my\s+bag\b|what\s+clubs)\b/i.test(low)) {
    const clubs = await currentBag();
    const msg = clubs.length ? `You have ${clubs.length} club${clubs.length !== 1 ? "s" : ""} in the bag:\n${clubs.map((c) => `• ${c}`).join("\n")}` : "Your bag is empty — try \"add driver to bag\".";
    return { reply: "", action: { kind: "none", message: msg } };
  }

  // 4) ADD TO BAG — "add <club> to bag".
  let m = t.match(/^(?:add|added|put)\s+(?:the\s+|a\s+|an\s+|my\s+)?(.+?)\s+(?:to|in|into)\s+(?:the\s+|my\s+)?bag\b/i);
  if (m) {
    const club = m[1].trim();
    if (!club) return { reply: "", action: { kind: "none", message: "Which club? Try \"add hybrid to bag\"." } };
    return { reply: "", action: { kind: "add_bag", input: { action: "added", club, changed_on: todayLocal() }, label: club } };
  }

  // 5) RETIRE FROM BAG — "retire <club>" / "retired <club> from bag".
  m = t.match(/^(?:retire|retired)\s+(?:the\s+|my\s+)?(.+?)(?:\s+from\s+(?:the\s+|my\s+)?bag)?\s*$/i);
  if (m) {
    const club = m[1].trim();
    if (club) return { reply: "", action: { kind: "add_bag", input: { action: "retired", club, changed_on: todayLocal() }, label: club } };
  }

  // 6) DELETE ROUND — requires the word "round"; the UI asks for confirmation before executing.
  m = t.match(/^(?:delete|remove)\s+(?:my\s+|the\s+)?(.+?)\s+round\b/i) || t.match(/^(?:delete|remove)\s+round\s+(?:at\s+)?(.+)$/i);
  if (m) {
    const rows = await findRounds(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await roundsNotFound(m[1]) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} rounds matching "${m[1].trim()}" — be more specific:\n${pickRounds(rows)}` } };
    return { reply: "", action: { kind: "delete_round", id: rows[0].id, label: `${rows[0].course} (${fmtShortDate(rows[0].played_on)})` } };
  }

  // 7) ADD ROUND — light local parse (no server call). Requires a leading verb.
  m = t.match(/^(?:add|log|new|track|record|played?|shot|scored)\s+(?:a\s+|an\s+)?(?:round\s+)?(.+)$/i);
  if (m) {
    const input = parseRound(m[1]);
    if (!input.course) return { reply: "", action: { kind: "none", message: "Which course? Try \"log Pebble Beach 82\"." } };
    if (input.total_score == null) return { reply: "", action: { kind: "none", message: `What did you shoot at ${input.course}? Try "log ${input.course} 82".` } };
    return { reply: "", action: { kind: "add_round", input, label: input.course } };
  }

  // 8) EDIT — putts / conditions / note on a resolved round.
  m = t.match(/^(?:set\s+)?putts?\s+(\d{1,3})\s+(?:for|at|on)\s+(.+)$/i);
  if (m) return editRound(m[2], { putts: parseInt(m[1], 10) }, (r) => `${r.course} → ${m![1]} putts`);
  m = t.match(/^(.+?)\s+putts?\s+(\d{1,3})\s*$/i);
  if (m) return editRound(m[1], { putts: parseInt(m[2], 10) }, (r) => `${r.course} → ${m![2]} putts`);
  m = t.match(/^(?:set\s+)?conditions?\s+(.+?)\s+(?:for|at|on)\s+(.+)$/i);
  if (m) return editRound(m[2], { conditions: m[1].trim() }, (r) => `${r.course} → ${m![1].trim()}`);
  m = t.match(/^(?:add\s+)?notes?\s+(?:to\s+|for\s+|on\s+)?(.+?)\s*[:\-]\s*(.+)$/i);
  if (m) return editRound(m[1], { notes: m[2].trim() }, (r) => `${r.course} note`);

  // 9) FILTER / SEARCH — local predicate over the archive.
  const filter = buildFilter(t, low);
  if (filter) return { reply: "", action: { kind: "filter", pred: filter.pred, label: filter.label } };

  // 10) Unclear → a short nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

async function editRound(term: string, patch: RoundPatch, label: (r: Round) => string): Promise<One> {
  const rows = await findRounds(term);
  if (rows.length === 0) return { reply: "", action: { kind: "none", message: await roundsNotFound(term) } };
  if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} rounds matching "${term.trim()}" — be more specific:\n${pickRounds(rows)}` } };
  return { reply: "", action: { kind: "edit_round", id: rows[0].id, patch, label: label(rows[0]) } };
}

function buildFilter(t: string, low: string): { pred: (r: Round) => boolean; label: string } | null {
  if (/\b9[\s-]*holes?\b|\b9h\b/i.test(low)) return { pred: (r) => r.holes_played === 9, label: "9-hole rounds" };
  if (/\b18[\s-]*holes?\b|\b18h\b/i.test(low)) return { pred: (r) => r.holes_played === 18, label: "18-hole rounds" };
  const m = t.match(/^(?:show|find|list|search)\s+(?:me\s+)?(?:all\s+)?(?:my\s+)?(?:rounds?\s+)?(?:at\s+|in\s+|with\s+|on\s+)?(.+)$/i);
  if (m) {
    const q = m[1].trim().toLowerCase().replace(/[.?!]+$/, "");
    if (!q) return null;
    if (/^rounds?$/.test(q)) return { pred: () => true, label: "all rounds" };
    return {
      pred: (r) =>
        r.course.toLowerCase().includes(q) || r.conditions.toLowerCase().includes(q) ||
        r.notes.toLowerCase().includes(q) || r.partners.some((p) => p.toLowerCase().includes(q)),
      label: q,
    };
  }
  return null;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
